/**
 * Instrument-Grade Motion Config
 * -------------------------------------------------
 * Shared Framer Motion presets. Every animation in the product
 * must come from here — never inline magic numbers.
 *
 * Principles:
 *   • Exponential easings only (no linear, no bounce on UI)
 *   • Short is sophisticated: 120ms / 240ms / 400ms / 800ms
 *   • Stagger lists/grids at 40ms between items
 *   • Transform + opacity only (perf: always 60fps)
 */

export const ease = {
  standard: [0.22, 1, 0.36, 1],   // easeOutQuart — default
  entrance: [0.16, 1, 0.3, 1],    // elements appearing
  exit: [0.7, 0, 0.84, 0],        // elements leaving
};

export const spring = {
  calm:   { type: 'spring', damping: 28, stiffness: 320, mass: 0.8 },
  snappy: { type: 'spring', damping: 26, stiffness: 400, mass: 0.7 },
  soft:   { type: 'spring', damping: 32, stiffness: 240, mass: 1.0 },
};

export const duration = {
  micro:  0.12,
  short:  0.24,
  medium: 0.40,
  long:   0.80,
};

// Entrance variants — drop-in for motion.div
export const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0, transition: { duration: duration.medium, ease: ease.entrance } },
  exit:    { opacity: 0, y: -8, transition: { duration: duration.short, ease: ease.exit } },
};

export const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: duration.short, ease: ease.standard } },
  exit:    { opacity: 0, transition: { duration: duration.micro } },
};

export const scaleIn = {
  initial: { opacity: 0, scale: 0.96 },
  animate: { opacity: 1, scale: 1, transition: { duration: duration.short, ease: ease.entrance } },
  exit:    { opacity: 0, scale: 0.98, transition: { duration: duration.micro } },
};

// Stagger container — use for lists/grids
export const staggerContainer = (staggerChildren = 0.04) => ({
  initial: {},
  animate: { transition: { staggerChildren, delayChildren: 0.05 } },
});

export const staggerChild = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: duration.short, ease: ease.entrance } },
};

// Drawer / modal slide-in
export const drawerRight = {
  initial: { x: '100%' },
  animate: { x: 0, transition: spring.calm },
  exit:    { x: '100%', transition: { duration: duration.short, ease: ease.exit } },
};

export const modalCenter = {
  initial: { opacity: 0, scale: 0.96, y: 8 },
  animate: { opacity: 1, scale: 1, y: 0, transition: { duration: duration.short, ease: ease.entrance } },
  exit:    { opacity: 0, scale: 0.98, transition: { duration: duration.micro } },
};

// Route transition
export const routeTransition = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.2, ease: ease.standard } },
  exit:    { opacity: 0, y: -4, transition: { duration: 0.15, ease: ease.exit } },
};

// Helper: reduced-motion safe variant — use via `prefersReducedMotion`
export const reducedSafe = (variant) => {
  if (typeof window === 'undefined') return variant;
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!prefersReduced) return variant;
  return {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.001 } },
    exit: { opacity: 0, transition: { duration: 0.001 } },
  };
};

export default {
  ease, spring, duration,
  fadeUp, fadeIn, scaleIn,
  staggerContainer, staggerChild,
  drawerRight, modalCenter, routeTransition,
  reducedSafe,
};
